/**
 * DataTable.jsx  —  Reusable Figma-matched table + pagination
 *   with drag-drop column reorder & column resize (mirror of DragTable.jsx)
 *
 * Props:
 *   columns            Array<{ id, label, render?(row, index) }>
 *   rows               Array<object>
 *   rowKey             (row, index) => string|number   [default: index]
 *   page               number   (0-based)
 *   rowsPerPage        number
 *   totalCount         number
 *   onPageChange       (event, newPage) => void
 *   onRowsPerPageChange(event) => void
 *   rowsPerPageOptions number[]   [default: [5,10,25,50,100]]
 *   showRowMenu        boolean    [default: true]
 *   onRowMenuClick     (row, index) => void
 *
 * Named exports:
 *   StatusBadge   — Verified / Unverified pill
 *   AvatarNameCell — avatar circle + name
 */

import React, { useCallback, useRef, useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import KeyboardArrowLeftIcon from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import "./Datatable.css";

/* ─── localStorage helpers ────────────────────────────────── */
const LS_PREFIX = "datatable_state_";

function saveToLS(tableId, colOrder, colWidths) {
  if (!tableId) return;
  try {
    localStorage.setItem(
      `${LS_PREFIX}${tableId}`,
      JSON.stringify({ colOrder, colWidths }),
    );
  } catch (_) { }
}

function loadFromLS(tableId) {
  if (!tableId) return null;
  try {
    const raw = localStorage.getItem(`${LS_PREFIX}${tableId}`);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

/* ─── StatusBadge ─────────────────────────────────────────── */
export function StatusBadge({ verified }) {
  return (
    <span
      className={`dt-status-badge ${verified ? "dt-verified" : "dt-unverified"}`}
    >
      {verified ? "Verified" : "Unverified"}
    </span>
  );
}

/* ─── AvatarNameCell ──────────────────────────────────────── */
export function AvatarNameCell({ imageUrl, name, onClick }) {
  return (
    <div className="dt-avatar-name-cell">
      {imageUrl ? (
        <img className="dt-avatar" src={imageUrl} alt={name || ""} />
      ) : (
        <span className="dt-avatar dt-avatar-placeholder">
          {name ? name.charAt(0).toUpperCase() : "?"}
        </span>
      )}
      <span className={onClick ? "dt-name-link" : "dt-name"} onClick={onClick}>
        {name}
      </span>
    </div>
  );
}

/* ─── Pagination ──────────────────────────────────────────── */
function DataTablePagination({
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions,
}) {
  const from = totalCount === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min(totalCount, (page + 1) * rowsPerPage);
  const isFirst = page === 0;
  const isLast = to >= totalCount;

  return (
    <div className="dt-pagination">
      <div className="dt-per-page-pill">
        <select
          className="dt-per-page-select-native"
          value={rowsPerPage}
          onChange={onRowsPerPageChange}
        >
          {rowsPerPageOptions.map((n) => (
            <option key={n} value={n}>
              {n} per page
            </option>
          ))}
        </select>
        <svg className="dt-per-page-chevron" viewBox="0 0 16 16" fill="none">
          <path
            d="M4 6l4 4 4-4"
            stroke="#6b7280"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* range pill */}
      <div className="dt-range-pill">
        {from}–{to} of {totalCount}
        <span className="dt-divider" />
        <button
          className="dt-arrow-btn"
          onClick={(e) => onPageChange(e, page - 1)}
          disabled={isFirst}
          aria-label="Previous page"
        >
          <KeyboardArrowLeftIcon className="dt-arrow-icon" />
        </button>
        <button
          className="dt-arrow-btn"
          onClick={(e) => onPageChange(e, page + 1)}
          disabled={isLast}
          aria-label="Next page"
        >
          <KeyboardArrowRightIcon className="dt-arrow-icon" />
        </button>
      </div>
    </div>
  );
}

/* ─── DraggableResizableHead ──────────────────────────────── */
function DraggableResizableHead({
  orderedCols,
  colWidths,
  startResize,
  handleColDragStart,
  handleColDragOver,
  handleColDrop,
  showRowMenu,
}) {
  const [activeResize, setActiveResize] = useState(null);
  const [hoveredResize, setHoveredResize] = useState(null);
  const [hoveredDrag, setHoveredDrag] = useState(null);

  const handleResizeMouseDown = (e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveResize(colKey);
    startResize(e, colKey);
    const onUp = () => {
      setActiveResize(null);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mouseup", onUp);
  };

  return (
    <>
      <style>{`
        .dt-resize-handle {
          position: absolute;
          right: 0;
          top: 0;
          height: 100%;
          width: 8px;
          cursor: col-resize;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .dt-resize-handle::after {
          content: '';
          display: block;
          width: 2px;
          height: 60%;
          border-radius: 2px;
          background: #d0d0d0;
          transition: background 0.15s, height 0.15s, width 0.15s;
        }
        .dt-resize-handle.hovered::after {
          background: #1492E6;
          height: 75%;
          width: 3px;
        }
        .dt-resize-handle.active::after {
          background: #0d6db5;
          height: 100%;
          width: 3px;
        }
        .dt-col-drag-zone {
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: grab;
          padding-right: 10px;
          user-select: none;
        }
        .dt-col-drag-zone:active { cursor: grabbing; }
        .dt-drag-dots {
          display: flex;
          flex-direction: column;
          gap: 2px;
          opacity: 0;
          transition: opacity 0.15s;
          flex-shrink: 0;
        }
        .dt-drag-dots span { display: flex; gap: 2px; }
        .dt-drag-dots span::before,
        .dt-drag-dots span::after {
          content: '';
          width: 3px;
          height: 3px;
          border-radius: 50%;
          background: #1492E6;
          display: block;
        }
        .dt-th-cell:hover .dt-drag-dots { opacity: 1; }
        .dt-th-cell.drag-over {
          background: #e8f4fd !important;
          border-left: 2px dashed #1492E6;
        }
        .dt-th-cell { transition: background 0.15s; }
        .dt-th-cell:hover { background: #f5f9ff; }
      `}</style>

      <TableHead>
        <TableRow className="dt-head-row">
          {orderedCols.map((col) => {
            const colKey = col._key;
            const width = colWidths[colKey] || 250;
            const isResizeHovered = hoveredResize === colKey;
            const isResizeActive = activeResize === colKey;
            const isDragHovered = hoveredDrag === colKey;

            /* ── Plain cell: no drag, no resize ── */
            if (col.disableDragResize) {
              return (
                <TableCell
                  key={colKey}
                  padding="none"
                  className={`dt-head-cell${col.headerClassName ? " " + col.headerClassName : ""}`}
                  style={{
                    width,
                    minWidth: 20,
                    boxSizing: "border-box",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    paddingLeft: "1rem",
                    paddingRight: "1rem",
                    textAlign: col.align || "left",
                    borderRight: "1px solid #D9D9D9",
                    background: "#F6F6F6",
                    ...(col.headerStyle || {}),
                  }}
                >
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      letterSpacing: "0.03em",
                    }}
                  >
                    {col.label}
                  </span>
                </TableCell>
              );
            }

            /* ── Normal cell: drag + resize ── */
            return (
              <TableCell
                key={colKey}
                padding="none"
                className={`dt-head-cell dt-th-cell${isDragHovered ? " drag-over" : ""}${col.headerClassName ? " " + col.headerClassName : ""}`}
                draggable
                onDragStart={() => handleColDragStart(col._idx)}
                onDragOver={(e) => {
                  e.preventDefault();
                  setHoveredDrag(colKey);
                  handleColDragOver(col._idx);
                }}
                onDragLeave={() => setHoveredDrag(null)}
                onDrop={() => {
                  setHoveredDrag(null);
                  handleColDrop();
                }}
                style={{
                  paddingLeft: "1rem",
                  paddingRight: "1rem",
                  width,
                  minWidth: 60,
                  position: "relative",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  boxSizing: "border-box",
                  borderRight: isResizeActive
                    ? "2px solid #1492E6"
                    : "1px solid #e0e0e0",
                  textAlign: col.align || "left",
                  ...(col.headerStyle || {}),
                }}
              >
                {/* Drag handle + label */}
                <div
                  className="dt-col-drag-zone"
                  style={{
                    justifyContent:
                      col.align === "center" ? "center" : undefined,
                  }}
                >
                  <div className="dt-drag-dots" title="Drag to reorder">
                    <span />
                    <span />
                    <span />
                  </div>
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      letterSpacing: "0.03em",
                    }}
                  >
                    {col.label}
                  </span>
                </div>

                <div
                  className={`dt-resize-handle${isResizeHovered ? " hovered" : ""}${isResizeActive ? " active" : ""}`}
                  onMouseDown={(e) => handleResizeMouseDown(e, colKey)}
                  onMouseEnter={() => setHoveredResize(colKey)}
                  onMouseLeave={() => !isResizeActive && setHoveredResize(null)}
                  title="Drag to resize column"
                />
              </TableCell>
            );
          })}

          {/* Menu column — not draggable/resizable */}
          {showRowMenu && (
            <TableCell className="dt-head-cell dt-menu-head-cell" />
          )}
        </TableRow>
      </TableHead>
    </>
  );
}

/* ─── DataTable (default export) ─────────────────────────── */
export default function DataTable({
  columns = [],
  rows = [],
  rowKey = (_, i) => i,
  tableId = "",
  page = 0,
  rowsPerPage = 10,
  totalCount = 0,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50, 100, 500, 1000],
  showRowMenu = true,
  onRowMenuClick,
  onColumnStateChange,
  hidePagination = false
}) {

  const [colOrder, setColOrder] = useState(() => {
    const saved = loadFromLS(tableId);
    if (saved?.colOrder && saved.colOrder.length === columns.length) {
      return saved.colOrder;
    }
    return columns.map((_, i) => i);
  });

  const [colWidths, setColWidths] = useState(() => {
    const saved = loadFromLS(tableId);
    return saved?.colWidths || {};
  });

  // Sync colOrder when columns length or tableId changes
  useEffect(() => {
    const saved = loadFromLS(tableId);
    if (saved?.colOrder && saved.colOrder.length === columns.length) {
      setColOrder(saved.colOrder);
    } else {
      setColOrder(columns.map((_, i) => i));
    }
  }, [tableId, columns.length]);

  // Sync colWidths when tableId changes
  useEffect(() => {
    const saved = loadFromLS(tableId);
    if (saved?.colWidths) {
      setColWidths(saved.colWidths);
    }
  }, [tableId]);

  /* ── Drag refs ── */
  const dragCol = useRef(null);
  const dragOverCol = useRef(null);

  const persist = useCallback(
    (nextOrder, nextWidths) => {
      saveToLS(tableId, nextOrder, nextWidths);
      if (typeof onColumnStateChange === "function") {
        onColumnStateChange({ colOrder: nextOrder, colWidths: nextWidths });
      }
    },
    [tableId, onColumnStateChange],
  );

  const startResize = (e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidths[colKey] || 250;

    const onMove = (moveEvt) => {
      const newW = Math.max(60, startW + (moveEvt.clientX - startX));
      setColWidths((prev) => {
        const next = { ...prev, [colKey]: newW };
        return next;
      });
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setColWidths((prev) => {
        persist(colOrder, prev);
        return prev;
      });
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleColDragStart = (idx) => {
    dragCol.current = idx;
  };

  const handleColDragOver = (idx) => {
    dragOverCol.current = idx;
  };

  const handleColDrop = () => {
    if (dragCol.current === null || dragOverCol.current === null) return;
    const order = [...colOrder];
    const from = order.indexOf(dragCol.current);
    const to = order.indexOf(dragOverCol.current);
    order.splice(to, 0, order.splice(from, 1)[0]);
    setColOrder(order);
    persist(order, colWidths);
    dragCol.current = null;
    dragOverCol.current = null;
  };

  const orderedCols = colOrder.map((i) => ({
    ...columns[i],
    _idx: i,
    _key: `${columns[i]?.id}_${i}`,
  }));
  console.log("orderedCols", orderedCols);
  return (
    <div className="dt-root">
      <TableContainer className="dt-table-container">
        <Table className="dt-table" aria-label="data table">
          <DraggableResizableHead
            orderedCols={orderedCols}
            colWidths={colWidths}
            startResize={startResize}
            handleColDragStart={handleColDragStart}
            handleColDragOver={handleColDragOver}
            handleColDrop={handleColDrop}
            showRowMenu={showRowMenu}
          />

          {/* BODY */}
          <TableBody>
            {rows.length > 0 ? (
              rows.map((row, index) => (
                <TableRow key={rowKey(row, index)} className="dt-body-row">
                  {orderedCols.map((col) => (
                    <TableCell
                      key={col._key}
                      className={`dt-body-cell${col.cellClassName ? " " + col.cellClassName : ""}`}
                      style={{
                        width: colWidths[col._key] || 250,
                        minWidth: 60,
                        maxWidth: colWidths[col._key] || 350,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        boxSizing: "border-box",
                        textAlign: col.align || "left",
                        ...(col.cellStyle || {}),
                      }}
                    >
                      {col.render ? col.render(row, index) : row[col.id]}
                    </TableCell>
                  ))}
                  {showRowMenu && (
                    <TableCell className="dt-body-cell dt-menu-cell">
                      <IconButton
                        size="small"
                        className="dt-menu-btn"
                        onClick={() =>
                          onRowMenuClick && onRowMenuClick(row, index)
                        }
                      >
                        <MoreVertIcon className="dt-menu-icon" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (showRowMenu ? 1 : 0)}
                  className="dt-empty-cell"
                >
                  No records found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {
        !hidePagination &&
        <DataTablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      }
    </div>
  );
}
